import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { bapi, fmtDate } from '@/lib/billingApi';
import { Segmented } from '../Segmented';
import { PLAN_LABEL, billingSource, planStatus } from './planText';

const pad = (n) => String(n).padStart(2, '0');
const isoDay = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const defaultEnd = (plan) => {
  const d = new Date();
  if (plan === 'pro_monthly') d.setMonth(d.getMonth() + 1); else d.setFullYear(d.getFullYear() + 1);
  return isoDay(d);
};
const dayLabel = (day) => fmtDate(`${day}T12:00:00`);

const summary = (plan, end, permanent) => {
  if (plan === 'free') return 'Pro features stop now. Their events and data stay.';
  if (permanent) return `${PLAN_LABEL[plan]} with no end date. It never expires.`;
  return end ? `${PLAN_LABEL[plan]} until ${dayLabel(end)}. After that the account goes back to Free unless they subscribe.` : 'Pick an end date.';
};

export const EditPlanDialog = ({ user: u, onClose, onSaved }) => {
  const isPro = u.subscription?.startsWith('pro_');
  const isPermanent = isPro && !u.subscription_expires;
  const keepDate = isPro && u.subscription_expires && new Date(u.subscription_expires) > new Date();
  const [plan, setPlan] = useState(isPermanent || keepDate ? u.subscription : 'pro_annual');
  const [end, setEnd] = useState(keepDate ? u.subscription_expires.slice(0, 10) : defaultEnd(isPermanent ? u.subscription : 'pro_annual'));
  const [permanent, setPermanent] = useState(isPermanent);
  const [busy, setBusy] = useState(false);
  const pro = plan !== 'free';

  const pick = (p) => {
    if (p === plan) return;
    setPlan(p);
    if (p !== 'free') { setEnd(defaultEnd(p)); setPermanent(false); }
  };
  const save = async () => {
    setBusy(true);
    try {
      await bapi(`/admin/users/${u.user_id}/upgrade`, { method: 'PUT', body: { subscription: plan, expires_at: !pro || permanent ? null : end } });
      toast.success(`${u.email}: ${PLAN_LABEL[plan]}${pro ? (permanent ? ' (permanent)' : ` until ${dayLabel(end)}`) : ''}`);
      onSaved();
    } catch (e) { toast.error(e.message); setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
      <DialogContent className="sm:max-w-md" data-testid="edit-plan-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl font-black uppercase text-[#00247D]" style={{ fontFamily: 'Barlow Condensed' }}>Edit plan</DialogTitle>
          <DialogDescription className="break-all">{u.name ? `${u.name} · ` : ''}{u.email}</DialogDescription>
        </DialogHeader>
        <p className="text-sm text-slate-600" data-testid="edit-plan-current">Now: <b className="text-slate-800">{planStatus(u)}</b></p>
        {isPro && billingSource(u) && (
          <p className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900" data-testid="edit-plan-billing-note">
            This user pays by {billingSource(u)}. If that subscription is still running, change it in {billingSource(u) === 'Card' ? 'Sales → Subscriptions' : 'PayPal'} instead.
          </p>
        )}
        <div>
          <Label>New plan</Label>
          <Segmented value={plan} onChange={pick} testId="edit-plan-option" options={Object.entries(PLAN_LABEL)} />
        </div>
        {pro && (
          <div className="space-y-3">
            <div>
              <Label htmlFor="edit-plan-end-date">Pro ends on</Label>
              <Input id="edit-plan-end-date" type="date" value={end} min={isoDay(new Date())} disabled={permanent} onChange={(e) => setEnd(e.target.value)} className="mt-1" data-testid="edit-plan-end-date" />
            </div>
            <label htmlFor="edit-plan-permanent" className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <Checkbox id="edit-plan-permanent" checked={permanent} onCheckedChange={(v) => setPermanent(!!v)} data-testid="edit-plan-permanent" />Permanent (no end date)
            </label>
          </div>
        )}
        <p className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-sm text-slate-600" data-testid="edit-plan-summary">{summary(plan, end, permanent)} Nothing is charged.</p>
        <Button onClick={save} disabled={busy || (pro && !permanent && !end)} className="w-full h-11 bg-[#00247D] hover:bg-[#001A5C] font-bold" data-testid="edit-plan-save">
          {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Save plan
        </Button>
      </DialogContent>
    </Dialog>
  );
};
